import { safeFetch } from 'lib/requests/fetch';
import {
  IS3FileMultipartUploadPartWithEtag,
  IS3FileMultipartUploadPartWithPresignedUrl,
} from 'lib/types/s3-files';

/**
 * POST/PUT/DELETE
 */
export interface ICreateMultipartUploadPayload {
  fileName: string;
  folder: string;
  numberOfParts: number;
}

export interface IVideoCreateMultipartUploadPayload {
  fileName: string;
  numberOfParts: number;
  videoFileExtension: string;
  durationInSeconds: number;
  language: string;
}

export interface IPolicyBotCreateMultipartUploadPayload {
  fileName: string;
  fileExtension: string;
  numberOfParts: number;
}

export interface IPrivateCreateMultipartUploadResponse {
  parts: IS3FileMultipartUploadPartWithPresignedUrl[];
  privateFileId: number;
  uploadId: string;
}

export interface ISensitiveCreateMultipartUploadResponse {
  parts: IS3FileMultipartUploadPartWithPresignedUrl[];
  sensitiveFileId: number;
  uploadId: string;
}

export interface IPublicCreateMultipartUploadResponse {
  parts: IS3FileMultipartUploadPartWithPresignedUrl[];
  publicFileId: number;
  uploadId: string;
}

// create response
export interface IVideoCreateMultipartUploadResponse {
  videoId: number;
  videoMultipartUploadId: string;
  videoUploadParts: IS3FileMultipartUploadPartWithPresignedUrl[];
}

export interface IPolicyBotCreateMultipartUploadResponse {
  id: number;
  multipartUploadId: string;
  multipartUploadParts: IS3FileMultipartUploadPartWithPresignedUrl[];
}

export interface IPrivateCompleteMultipartUploadPayload {
  parts: IS3FileMultipartUploadPartWithEtag[];
  privateFileId: number;
  uploadId: string;
}

export interface ISensitiveCompleteMultipartUploadPayload {
  parts: IS3FileMultipartUploadPartWithEtag[];
  sensitiveFileId: number;
  uploadId: string;
}

export interface IPublicCompleteMultipartUploadPayload {
  parts: IS3FileMultipartUploadPartWithEtag[];
  publicFileId: number;
  uploadId: string;
}

export interface IVideoCompleteMultipartUploadPayload {
  videoId: number;
  uploadId?: string;
  parts?: IS3FileMultipartUploadPartWithEtag[];
}

export interface IPolicyBotCompleteMultipartUploadPayload {
  id: number;
  multipartUploadId: string;
  multipartUploadParts: IS3FileMultipartUploadPartWithEtag[];
  name: string;
}

export interface IPrivateCompleteMultipartUploadResponse {
  privateFileId: number;
}

export interface ISensitiveCompleteMultipartUploadResponse {
  sensitiveFileId: number;
}

export interface IPublicCompleteMultipartUploadResponse {
  fileName: string;
  fileUrl: string;
  publicFileId: number;
}

export interface IAdminAudioDescriptionVideo {
  id: number;
  durationInSeconds: number;
  videoUrl: string;
  hlsManifestUrl?: string;
  dashManifestUrl?: string;
  mp4Urls: Array<{
    quality: number;
    url: string;
  }>;
}

export interface IAdminCaption {
  id: number;
  originalFileName: string;
  language: string;
  url: string;
  uploadedAt: string;
}

export interface IAdminUploadedVideoFile {
  id: number;
  name: string;
  language: string;
  durationInSeconds: number;
  videoUrl: string;
  hlsManifestUrl?: string;
  dashManifestUrl?: string;
  audioDescriptionVideo?: IAdminAudioDescriptionVideo;
  captions: IAdminCaption[];
  mp4Urls: Array<{
    quality: number;
    url: string;
  }>;
}

export const s3PublicCreateMultipartUpload = (
  url: string,
  payload: ICreateMultipartUploadPayload
) => {
  return safeFetch<IPublicCreateMultipartUploadResponse>(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const s3PublicCompleteMultipartUpload = (
  url: string,
  payload: IPublicCompleteMultipartUploadPayload
) => {
  return safeFetch<IPublicCompleteMultipartUploadResponse>(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const s3PrivateCreateMultipartUpload = (
  payload: ICreateMultipartUploadPayload
) => {
  return safeFetch<IPrivateCreateMultipartUploadResponse>(
    '/admin/files/private-multipart-upload',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

export const s3PrivateCompleteMultipartUpload = (
  payload: IPrivateCompleteMultipartUploadPayload
) => {
  return safeFetch<IPrivateCompleteMultipartUploadResponse>(
    '/admin/files/complete-private-upload',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

export const s3PolicyBotCreateMultipartUpload = (
  payload: IPolicyBotCreateMultipartUploadPayload
) => {
  return safeFetch<IPolicyBotCreateMultipartUploadResponse>(
    '/admin/account/policy-bot/files',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

/**
 * @deprecated
 */
export const s3SensitiveCreateMultipartUpload = (
  companyUrlAlias: string,
  payload: ICreateMultipartUploadPayload
) => {
  return safeFetch<ISensitiveCreateMultipartUploadResponse>(
    `/concerns/${companyUrlAlias}/multipart-upload`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

/**
 * @deprecated
 */
export const s3SensitiveCompleteMultipartUpload = (
  companyUrlAlias: string,
  payload: ISensitiveCompleteMultipartUploadPayload
) => {
  return safeFetch<ISensitiveCompleteMultipartUploadResponse>(
    `/concerns/${companyUrlAlias}/complete-upload`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

export const s3CreateMultipartVideoUpload = (
  payload: IVideoCreateMultipartUploadPayload
) => {
  return safeFetch<IVideoCreateMultipartUploadResponse>(
    '/admin/files/video-multipart-upload',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

export const s3PolicyBotCompleteMultipartUpload = (
  payload: IPolicyBotCompleteMultipartUploadPayload
) => {
  return safeFetch<void>('/admin/account/policy-bot/files/complete-upload', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const s3CompleteMultipartVideoUpload = (
  payload: IVideoCompleteMultipartUploadPayload
) => {
  return safeFetch<IAdminUploadedVideoFile>(
    '/admin/files/complete-video-upload',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

export const s3PresignedUrlUpload = async ({
  url,
  blob,
  onSuccess,
  options,
}: {
  url: string;
  blob: Blob;
  onSuccess: () => void;
  options?: Partial<Request>;
}): Promise<Response> => {
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    ...options,
  });
  onSuccess();
  return res;
};
